import { useState, useEffect } from 'react';
import { linesApi, inspectionsApi, type ProductionLine, type Inspection } from '../services/api';

export function Inspections() {
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [selectedLine, setSelectedLine] = useState<string>('');
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [resultFilter, setResultFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingInspections, setLoadingInspections] = useState(false);

  useEffect(() => {
    linesApi.list()
      .then((data) => {
        setLines(data);
        if (data.length > 0) setSelectedLine(data[0]!.id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedLine) return;
    setLoadingInspections(true);
    inspectionsApi.list({
      lineId: selectedLine,
      result: resultFilter || undefined,
      limit: 100,
    })
      .then((data) => setInspections(data.inspections))
      .catch(console.error)
      .finally(() => setLoadingInspections(false));
  }, [selectedLine, resultFilter]);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <>
      <div className="page-header">
        <h1>Inspections</h1>
        <p>Browse and filter inspection results across production lines</p>
      </div>

      <div className="page-body">
        {/* Filters */}
        <div className="flex gap-4 mb-6" style={{ flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
            <label className="form-label">Production Line</label>
            <select
              className="form-select"
              value={selectedLine}
              onChange={(e) => setSelectedLine(e.target.value)}
            >
              {lines.map((line) => (
                <option key={line.id} value={line.id}>{line.name} ({line.code})</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 150 }}>
            <label className="form-label">Result</label>
            <select
              className="form-select"
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
            >
              <option value="">All Results</option>
              <option value="pass">Pass Only</option>
              <option value="fail">Fail Only</option>
              <option value="warning">Warning Only</option>
            </select>
          </div>
        </div>

        {/* Results Table */}
        {loadingInspections ? (
          <div className="loading-page"><div className="spinner" /></div>
        ) : inspections.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
            <h3>No Inspections Found</h3>
            <p>Inspection data will appear here once your edge devices start reporting results.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Result</th>
                  <th>Defect Type</th>
                  <th>Confidence</th>
                  <th>Speed (ppm)</th>
                  <th>Package #</th>
                  <th>Anomaly Score</th>
                </tr>
              </thead>
              <tbody>
                {inspections.map((insp) => (
                  <tr key={insp.id}>
                    <td className="text-sm font-mono">
                      {new Date(insp.inspectedAt).toLocaleString()}
                    </td>
                    <td>
                      <span className={`badge badge-${insp.result}`}>
                        {insp.result.toUpperCase()}
                      </span>
                    </td>
                    <td>{insp.defectType ? formatDefectType(insp.defectType) : '—'}</td>
                    <td className="font-mono">
                      {insp.confidenceScore ? `${(insp.confidenceScore * 100).toFixed(0)}%` : '—'}
                    </td>
                    <td className="font-mono">{insp.lineSpeedPpm || '—'}</td>
                    <td className="font-mono">{insp.packageIndex ?? '—'}</td>
                    <td className="font-mono">
                      {insp.thermalMetrics ? (
                        <span style={{
                          color: insp.thermalMetrics.anomalyScore > 0.8 ? 'var(--status-fail)' :
                                 insp.thermalMetrics.anomalyScore > 0.5 ? 'var(--status-warning)' : 'var(--status-pass)'
                        }}>
                          {insp.thermalMetrics.anomalyScore.toFixed(3)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-sm text-muted mt-4">
          Showing {inspections.length} inspection{inspections.length !== 1 ? 's' : ''}
        </div>
      </div>
    </>
  );
}

function formatDefectType(type: string): string {
  const labels: Record<string, string> = {
    contamination: 'Contamination', wrinkle: 'Wrinkle / Fold',
    under_heating: 'Under-Heating', over_heating: 'Over-Heating',
    incomplete_seal: 'Incomplete Seal', misalignment: 'Misalignment', other: 'Other',
  };
  return labels[type] || type;
}
